import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DesignSystemSurface } from '../surface/DesignSystemSurface';
import {
  AssetThumb,
  BeatCard,
  BeatStrip,
  BeatState,
  GenerateButton,
  GenerationsRail,
  GenerationSession,
  InspectorPanel,
  InspectorSection,
  PromptComposer,
  StudioCanvas,
  StudioLayout,
} from '../studio';
import { Tag } from '../components';

const gradientThumb = (from: string, to: string): string => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 90 160'>
    <defs>
      <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${from}'/>
        <stop offset='100%' stop-color='${to}'/>
      </linearGradient>
    </defs>
    <rect width='90' height='160' fill='url(#g)'/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

type Beat = {
  id: string;
  caption: string;
  state: BeatState;
  variants: { id: string; src: string }[];
  activeVariantIndex: number;
  locked: boolean;
  durationMs: number;
};

const buildBeats = (): Beat[] => [
  {
    id: 'beat-1',
    caption: 'Open on a quiet street at sunrise, mist still hanging in the air.',
    state: 'ready',
    variants: [
      { id: 'v1a', src: gradientThumb('#fdba74', '#f97316') },
      { id: 'v1b', src: gradientThumb('#fed7aa', '#ea580c') },
      { id: 'v1c', src: gradientThumb('#fda4af', '#ea580c') },
      { id: 'v1d', src: gradientThumb('#fde68a', '#f97316') },
    ],
    activeVariantIndex: 0,
    locked: true,
    durationMs: 3200,
  },
  {
    id: 'beat-2',
    caption: 'The protagonist laces up worn running shoes on a wooden porch.',
    state: 'ready',
    variants: [
      { id: 'v2a', src: gradientThumb('#a78bfa', '#6d28d9') },
      { id: 'v2b', src: gradientThumb('#c4b5fd', '#7c3aed') },
    ],
    activeVariantIndex: 1,
    locked: false,
    durationMs: 2800,
  },
  {
    id: 'beat-3',
    caption: 'Slow push-in as steam rises from a coffee mug on a table.',
    state: 'generating',
    variants: [],
    activeVariantIndex: 0,
    locked: false,
    durationMs: 3000,
  },
  {
    id: 'beat-4',
    caption: 'Wide shot, runner hits the trail. Frost crunches under each step.',
    state: 'ready',
    variants: [
      { id: 'v4a', src: gradientThumb('#86efac', '#16a34a') },
      { id: 'v4b', src: gradientThumb('#bbf7d0', '#15803d') },
      { id: 'v4c', src: gradientThumb('#a7f3d0', '#0d9488') },
    ],
    activeVariantIndex: 0,
    locked: false,
    durationMs: 3400,
  },
  {
    id: 'beat-5',
    caption: 'Closing line over a long horizon shot. Hold for the beat.',
    state: 'empty',
    variants: [],
    activeVariantIndex: 0,
    locked: false,
    durationMs: 3000,
  },
];

const buildHistory = (): GenerationSession[] => [
  {
    id: 'today',
    title: 'Today',
    entries: [
      { id: 'h1', kind: 'image', src: gradientThumb('#fb923c', '#ea580c'), label: 'Sunrise sprint', pinned: true },
      { id: 'h2', kind: 'image', src: gradientThumb('#a78bfa', '#6d28d9'), label: 'Mist & laces' },
      { id: 'h3', kind: 'image', src: gradientThumb('#86efac', '#16a34a'), label: 'Trail steam' },
      { id: 'h4', kind: 'image', src: gradientThumb('#fda4af', '#dc2626'), label: 'Frost cold open' },
    ],
  },
  {
    id: 'yesterday',
    title: 'Yesterday',
    entries: [
      { id: 'h5', kind: 'image', src: gradientThumb('#94a3b8', '#475569'), label: 'Dawn rituals v2' },
      { id: 'h6', kind: 'image', src: gradientThumb('#fcd34d', '#d97706'), label: 'Honey light' },
    ],
  },
  {
    id: 'older',
    title: 'Earlier this week',
    entries: [
      { id: 'h7', kind: 'image', src: gradientThumb('#67e8f9', '#0e7490'), label: 'Lake mornings' },
      { id: 'h8', kind: 'image', src: gradientThumb('#f9a8d4', '#be185d'), label: 'Sunset cool-down' },
    ],
  },
];

const StyleChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
`;

const StyleChip = styled.button<{ $active: boolean }>`
  appearance: none;
  border-radius: var(--radius-pill);
  border: 1px solid
    ${(p) => (p.$active ? 'var(--color-accent)' : 'var(--color-border-subtle)')};
  background: ${(p) => (p.$active ? 'var(--color-accent-subtle)' : 'var(--color-surface)')};
  color: ${(p) => (p.$active ? 'var(--color-accent-text)' : 'var(--color-text-secondary)')};
  padding: 4px var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const AspectRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
`;

const AspectButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border-radius: var(--radius-md);
  border: 1px solid
    ${(p) => (p.$active ? 'var(--color-accent)' : 'var(--color-border-subtle)')};
  background: ${(p) => (p.$active ? 'var(--color-accent-subtle)' : 'var(--color-surface)')};
  color: ${(p) => (p.$active ? 'var(--color-accent-text)' : 'var(--color-text-secondary)')};
  padding: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const AspectGlyph = styled.div<{ $w: number; $h: number }>`
  width: ${(p) => p.$w}px;
  height: ${(p) => p.$h}px;
  border-radius: var(--radius-sm);
  border: 1.5px solid currentColor;
`;

const RefThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
`;

const TopBarTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopBarHeading = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const TopBarSub = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const STYLES = ['Cinematic', 'Documentary', 'Hard truth', 'UGC rant', 'Day in life', 'Tutorial'];
const ASPECTS: { id: '9:16' | '1:1' | '16:9'; label: string; w: number; h: number }[] = [
  { id: '9:16', label: 'Reel', w: 18, h: 28 },
  { id: '1:1', label: 'Square', w: 24, h: 24 },
  { id: '16:9', label: 'Wide', w: 28, h: 16 },
];

export const StudioDemo: React.FC = () => {
  const [beats, setBeats] = useState<Beat[]>(buildBeats);
  const [activeBeatId, setActiveBeatId] = useState<string>('beat-1');
  const [prompt, setPrompt] = useState<string>(
    'A grounded short film about an early-morning runner. Cinematic, golden hour, soft handheld.'
  );
  const [activeStyle, setActiveStyle] = useState<string>('Cinematic');
  const [aspect, setAspect] = useState<'9:16' | '1:1' | '16:9'>('9:16');
  const [variantCount, setVariantCount] = useState<number>(4);
  const [activeHistoryId, setActiveHistoryId] = useState<string | undefined>('h1');
  const [search, setSearch] = useState<string>('');
  const [sessions, setSessions] = useState<GenerationSession[]>(buildHistory);

  const activeBeat = useMemo(
    () => beats.find((b) => b.id === activeBeatId) ?? beats[0],
    [beats, activeBeatId]
  );

  const totalDurationMs = useMemo(
    () => beats.reduce((sum, b) => sum + b.durationMs, 0),
    [beats]
  );

  const updateBeat = (id: string, patch: Partial<Beat>) => {
    setBeats((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const filteredSessions = useMemo(() => {
    if (!search.trim()) return sessions;
    const q = search.toLowerCase();
    return sessions
      .map((s) => ({
        ...s,
        entries: s.entries.filter((e) => (e.label ?? '').toLowerCase().includes(q)),
      }))
      .filter((s) => s.entries.length > 0);
  }, [search, sessions]);

  const handleTogglePin = (entryId: string) => {
    setSessions((prev) =>
      prev.map((s) => ({
        ...s,
        entries: s.entries.map((e) =>
          e.id === entryId ? { ...e, pinned: !e.pinned } : e
        ),
      }))
    );
  };

  const canvasState =
    activeBeat.state === 'generating'
      ? 'generating'
      : activeBeat.state === 'empty'
      ? 'empty'
      : activeBeat.state === 'error'
      ? 'error'
      : 'ready';

  const activeVariant = activeBeat.variants[activeBeat.activeVariantIndex];

  return (
    <DesignSystemSurface>
      <StudioLayout
        railWidth={280}
        inspectorWidth={360}
        topbar={
          <TopBarTitle>
            <TopBarHeading>Media Studio</TopBarHeading>
            <TopBarSub>Mountain Mornings • Daily step • Cinematic reel</TopBarSub>
          </TopBarTitle>
        }
        rail={
          <GenerationsRail
            sessions={filteredSessions}
            activeEntryId={activeHistoryId}
            searchValue={search}
            onSearchChange={setSearch}
            onEntryClick={(entry) => setActiveHistoryId(entry.id)}
            onTogglePin={(entry) => handleTogglePin(entry.id)}
          />
        }
        inspector={
          <InspectorPanel
            title="Prompt & style"
            subtitle="Drives the next generation pass"
            footer={
              <GenerateButton
                variantCount={variantCount}
                onVariantCountChange={setVariantCount}
                onClick={() => {
                  updateBeat(activeBeatId, { state: 'generating' });
                }}
                hint={`⌘↵ to run · uses ${variantCount} credit${variantCount === 1 ? '' : 's'}`}
              />
            }
          >
            <InspectorSection title="Prompt">
              <PromptComposer
                value={prompt}
                onChange={setPrompt}
                charLimit={500}
                chips={[
                  { id: 'voice', label: 'Voice · Maya', icon: '🎙' },
                  { id: 'ref', label: '3 references', icon: '🖼' },
                ]}
                onSubmit={() => updateBeat(activeBeatId, { state: 'generating' })}
              />
            </InspectorSection>

            <InspectorSection title="Style" description="Affects all unlocked beats">
              <StyleChipRow>
                {STYLES.map((s) => (
                  <StyleChip
                    key={s}
                    type="button"
                    $active={activeStyle === s}
                    onClick={() => setActiveStyle(s)}
                  >
                    {s}
                  </StyleChip>
                ))}
              </StyleChipRow>
            </InspectorSection>

            <InspectorSection title="Aspect">
              <AspectRow>
                {ASPECTS.map((a) => (
                  <AspectButton
                    key={a.id}
                    type="button"
                    $active={aspect === a.id}
                    onClick={() => setAspect(a.id)}
                  >
                    <AspectGlyph $w={a.w} $h={a.h} />
                    <span>{a.label}</span>
                  </AspectButton>
                ))}
              </AspectRow>
            </InspectorSection>

            <InspectorSection
              title="References"
              description="Drag images to lock visual identity"
              collapsible
              defaultOpen
            >
              <RefThumbGrid>
                <AssetThumb src={gradientThumb('#fdba74', '#9a3412')} aspect="1:1" />
                <AssetThumb src={gradientThumb('#a78bfa', '#4c1d95')} aspect="1:1" />
                <AssetThumb src={gradientThumb('#86efac', '#14532d')} aspect="1:1" />
              </RefThumbGrid>
            </InspectorSection>

            <InspectorSection
              title="Advanced"
              description="Seed, sampler, fidelity"
              collapsible
              defaultOpen={false}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Tag variant="neutral" size="sm">seed: 42</Tag>
                <Tag variant="neutral" size="sm">sampler: dpm++</Tag>
                <Tag variant="neutral" size="sm">fidelity: 0.78</Tag>
              </div>
            </InspectorSection>
          </InspectorPanel>
        }
        beatStrip={
          <BeatStrip
            title="Storyboard"
            hint={`${beats.length} beats · variants ${variantCount}×`}
            totalDurationMs={totalDurationMs}
          >
            {beats.map((beat, index) => (
              <BeatCard
                key={beat.id}
                index={index}
                caption={beat.caption}
                state={beat.state}
                variants={beat.variants}
                activeVariantIndex={beat.activeVariantIndex}
                active={beat.id === activeBeatId}
                locked={beat.locked}
                durationMs={beat.durationMs}
                aspect={aspect}
                onSelect={() => setActiveBeatId(beat.id)}
                onVariantChange={(idx) => updateBeat(beat.id, { activeVariantIndex: idx })}
                onRegenerate={() => updateBeat(beat.id, { state: 'generating' })}
                onToggleLock={() => updateBeat(beat.id, { locked: !beat.locked })}
              />
            ))}
          </BeatStrip>
        }
      >
        <StudioCanvas
          aspect={aspect}
          state={canvasState}
          progressLabel={canvasState === 'generating' ? 'Rendering motion · 42%' : undefined}
          progressPercent={canvasState === 'generating' ? 42 : undefined}
        >
          {activeVariant && (
            <img src={activeVariant.src} alt={activeBeat.caption} />
          )}
        </StudioCanvas>
      </StudioLayout>
    </DesignSystemSurface>
  );
};

StudioDemo.displayName = 'StudioDemo';
